// engine/input.ts — keyboard/mouse state + VirtualInput injection for the test API.
// Gameplay actions read from the virtual object when it is enabled; meta keys
// (pause/restart/confirm) always read the real keyboard so a human can drive the
// shell while a bot drives the Warden.

import type { Vec, VirtualInput } from '../types'

export type Action =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'fire'
  | 'special'
  | 'dash'
  | 'quaff'
  | 'interact'

const ACTIONS: Action[] = ['up', 'down', 'left', 'right', 'fire', 'special', 'dash', 'quaff', 'interact']

export const virtual: VirtualInput = {
  enabled: false,
  move: { x: 0, y: 0 },
  aim: { x: 0, y: 0 },
  fire: false,
  special: false,
  dash: false,
  quaff: false,
  interact: false,
}

const keys = new Set<string>()
const mouseButtons = new Set<number>()
const mouse: Vec = { x: 0, y: 0 } // client px
let firstInputFired = false
let onFirstInput: (() => void) | null = null

// Meta keys: edge-detected against a per-frame drain, independent of the sim step.
const metaPressed = new Set<string>()
let anyPressedFlag = false

const cur: Record<string, boolean> = {}
const prev: Record<string, boolean> = {}

function keyDownRaw(code: string): boolean {
  return keys.has(code)
}

function markInput(): void {
  if (!firstInputFired) {
    firstInputFired = true
    onFirstInput?.()
  }
  anyPressedFlag = true
}

export function initInput(canvas: HTMLCanvasElement, firstInputCb: () => void): void {
  onFirstInput = firstInputCb

  window.addEventListener('keydown', (e) => {
    if (e.repeat) {
      markInput()
      return
    }
    keys.add(e.code)
    metaPressed.add(e.code)
    markInput()
    // Stop the page from scrolling / quick-finding under the game.
    if (
      e.code === 'Space' ||
      e.code.startsWith('Arrow') ||
      e.code === 'Tab' ||
      e.code === 'Slash'
    ) {
      e.preventDefault()
    }
  })
  window.addEventListener('keyup', (e) => keys.delete(e.code))
  window.addEventListener('blur', () => {
    keys.clear()
    mouseButtons.clear()
  })

  canvas.addEventListener('mousemove', (e) => {
    mouse.x = e.clientX
    mouse.y = e.clientY
  })
  canvas.addEventListener('mousedown', (e) => {
    mouseButtons.add(e.button)
    markInput()
    e.preventDefault()
  })
  window.addEventListener('mouseup', (e) => mouseButtons.delete(e.button))
  canvas.addEventListener('contextmenu', (e) => e.preventDefault())
  window.addEventListener('pointerdown', () => markInput())
}

function rawAction(a: Action): boolean {
  switch (a) {
    case 'up':
      return keyDownRaw('KeyW') || keyDownRaw('ArrowUp')
    case 'down':
      return keyDownRaw('KeyS') || keyDownRaw('ArrowDown')
    case 'left':
      return keyDownRaw('KeyA') || keyDownRaw('ArrowLeft')
    case 'right':
      return keyDownRaw('KeyD') || keyDownRaw('ArrowRight')
    case 'fire':
      return mouseButtons.has(0)
    case 'special':
      return mouseButtons.has(2)
    case 'dash':
      return keyDownRaw('Space')
    case 'quaff':
      return keyDownRaw('KeyQ')
    case 'interact':
      return keyDownRaw('KeyE')
  }
}

/** Call exactly once per simulation step, before anything reads input. */
export function sample(): void {
  const v = virtual.enabled
  for (let i = 0; i < ACTIONS.length; i++) {
    const a = ACTIONS[i]
    prev[a] = cur[a] === true
    if (v) {
      switch (a) {
        case 'up':
          cur[a] = virtual.move.y < -0.2
          break
        case 'down':
          cur[a] = virtual.move.y > 0.2
          break
        case 'left':
          cur[a] = virtual.move.x < -0.2
          break
        case 'right':
          cur[a] = virtual.move.x > 0.2
          break
        default:
          cur[a] = virtual[a] === true
      }
    } else {
      cur[a] = rawAction(a)
    }
  }
}

export function isDown(a: Action): boolean {
  return cur[a] === true
}

export function wasPressed(a: Action): boolean {
  return cur[a] === true && prev[a] !== true
}

/** -1..1 axes, length-clamped to 1. */
export function moveVec(out: Vec): Vec {
  if (virtual.enabled) {
    out.x = Math.max(-1, Math.min(1, virtual.move.x))
    out.y = Math.max(-1, Math.min(1, virtual.move.y))
  } else {
    out.x = (isDown('right') ? 1 : 0) - (isDown('left') ? 1 : 0)
    out.y = (isDown('down') ? 1 : 0) - (isDown('up') ? 1 : 0)
  }
  const l = Math.hypot(out.x, out.y)
  if (l > 1) {
    out.x /= l
    out.y /= l
  }
  return out
}

export function mouseClient(out: Vec): Vec {
  out.x = mouse.x
  out.y = mouse.y
  return out
}

export function usingVirtualAim(): boolean {
  return virtual.enabled
}

export function virtualAim(out: Vec): Vec {
  out.x = virtual.aim.x
  out.y = virtual.aim.y
  return out
}

/** Real-keyboard edge, consumed on read. Used by shell screens (title/summary/pause). */
export function metaKeyPressed(code: string): boolean {
  if (metaPressed.has(code)) {
    metaPressed.delete(code)
    return true
  }
  return false
}

/** Any real input since the last check, consumed on read. */
export function anyKeyPressed(): boolean {
  if (anyPressedFlag) {
    anyPressedFlag = false
    metaPressed.clear()
    return true
  }
  return false
}

export function clearMetaKeys(): void {
  metaPressed.clear()
  anyPressedFlag = false
}

export function resetVirtual(): void {
  virtual.enabled = false
  virtual.move.x = 0
  virtual.move.y = 0
  virtual.fire = false
  virtual.special = false
  virtual.dash = false
  virtual.quaff = false
  virtual.interact = false
}
